import {
  BaseModel,
  createProvidedModelContext,
  EventWatchable,
  TargetableModel,
  Watchable,
  type DeveloperDetails,
} from '@cotera/client/v0/actions/framework';
import type {
  ReadonlyEventWatchable,
  ReadonlyWatchable,
  WatchableGetter,
} from '@cotera/client/v0/actions/framework/watchable';
import { ResourceType, BroadcastEventType } from '@cotera/api';
import type { BroadcastEventPayloadMap } from '@cotera/api';
import { err, ok, type Result } from 'neverthrow';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import type {
  DatasetColumnSettingOutput,
  DatasetOutput,
  ResourceMeta,
  TenantedClient,
} from '@cotera/client/app/etc/api/typed-client';
import { WatchablesFactory } from '../watchables/watchables-factory';
import {
  DATASET_IMPORT_EVENT,
  LOCAL_DATASET_IMPORT_EVENT,
  datasetImportSignalFromPayload,
  datasetImportViewFromStatus,
  inFlightDatasetUpload,
  isDatasetImportPayloadFor,
  nextDatasetImportSequence,
  type DatasetImportView,
  type StampedDatasetImportSignal,
} from './dataset-import';
import type { DatasetPreviewQueryResult } from './preview/types';

export type DatasetResourceApiData = {
  dataset: DatasetOutput;
  meta: ResourceMeta | null;
};

type DatasetResourceState = {
  resource: DatasetResourceApiData | null;
  status: 'loading' | 'ready' | 'not-found';
};

/**
 * Only what the model actually calls, so a test can stand one up without
 * building a whole tenanted client. The page passes the real thing.
 */
export type DatasetModelClient = {
  resource: Pick<TenantedClient['resource'], 'get'> & {
    dataset: Pick<
      TenantedClient['resource']['dataset'],
      'createManual' | 'updateSettings' | 'updateColumnSettings'
    >;
  };
};

type DatasetModelProps = {
  datasetId: string;
  client: DatasetModelClient;
  watchablesFactory: Pick<WatchablesFactory, 'makeResourceUpdatedWatchable'>;
  /** Identity for the placeholder an unsaved dataset renders from. */
  orgId: string;
  userId: string;
  /** Opened from "New dataset": nothing exists server-side yet. */
  isNew?: boolean;
};

export type ManualDatasetColumnInput = {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'date';
};

/** The dataset an unsaved page renders from, before anything is persisted. */
function newDatasetPlaceholder(params: {
  datasetId: string;
  orgId: string;
  userId: string;
}): DatasetOutput {
  const now = new Date().toISOString();
  return {
    id: params.datasetId,
    orgId: params.orgId,
    ownerId: params.userId,
    type: 'manual',
    name: 'Untitled dataset',
    originalFilename: null,
    mimeType: null,
    sizeBytes: null,
    preview: { queryable: true, query: null },
    status: 'active',
    createdAt: now,
    updatedAt: now,
    artifacts: [],
  };
}

function datasetArtifactsSignature(dataset: DatasetOutput | undefined): string {
  return JSON.stringify(
    (dataset?.artifacts ?? []).map((artifact) => ({
      id: artifact.id,
      hash: artifact.hash,
      updatedAt: artifact.updatedAt,
      metadata: artifact.metadata ?? null,
    }))
  );
}

function datasetStateEquality(
  a: DatasetResourceState,
  b: DatasetResourceState
): boolean {
  if (a.status !== b.status) {
    return false;
  }
  const aDataset = a.resource?.dataset;
  const bDataset = b.resource?.dataset;
  return (
    aDataset?.id === bDataset?.id &&
    aDataset?.name === bDataset?.name &&
    aDataset?.updatedAt === bDataset?.updatedAt &&
    // Promoting a chat-scoped dataset flips this without touching most fields;
    // without it a promote refetch is dropped as "unchanged" and the "Save as
    // dataset" affordance never clears.
    aDataset?.chatScoped === bDataset?.chatScoped &&
    datasetArtifactsSignature(aDataset) ===
      datasetArtifactsSignature(bDataset) &&
    a.resource?.meta?.folderId === b.resource?.meta?.folderId &&
    a.resource?.meta?.folderScope === b.resource?.meta?.folderScope
  );
}

/** The preview's default source when a dataset has no saved query. */
export const DEFAULT_DATASET_QUERY = 'SELECT * FROM dataset';

/**
 * How often a page showing an import re-checks whether it actually finished.
 *
 * Long, because it only exists for the case where an event went missing: the
 * ordinary path is a `ready` broadcast landing in milliseconds. Short enough
 * that a page left on a stale spinner rights itself while the user is still
 * looking at it.
 */
const DATASET_IMPORT_RECONCILE_MS = 15_000;

export class DatasetModel extends BaseModel implements TargetableModel {
  private _isTargeted = true;
  readonly datasetId: string;
  readonly datasetDataWatchable: ReadonlyEventWatchable<DatasetResourceState>;
  /**
   * The query the grid is currently reading through. Editing it is a local
   * change until saved, which is what drives the unsaved indicator and Ctrl+S,
   * the same way agent and document edits do.
   */
  readonly previewQueryWatchable: Watchable<string>;
  readonly savedPreviewQueryWatchable: Watchable<string>;
  readonly hasUnsavedChangesWatchable: ReadonlyWatchable<boolean>;
  /**
   * False until the dataset exists server-side. A new dataset renders from a
   * placeholder until the user does something worth keeping.
   */
  readonly isPersistedWatchable: Watchable<boolean>;
  /**
   * Where an in-flight CSV import has got to, or null when there is none.
   *
   * Composed from three sources because no one of them is sufficient: the
   * fetched resource's `importStatus` is all a page loaded mid-import has, the
   * saga's broadcasts are what move it afterwards without refetching on every
   * phase, and this tab's own upload progress is the part no server can see.
   */
  readonly importWatchable: ReadonlyWatchable<DatasetImportView | null>;

  private readonly _remoteImport: ReadonlyEventWatchable<StampedDatasetImportSignal | null>;
  private readonly _localImport: ReadonlyEventWatchable<StampedDatasetImportSignal | null>;

  private readonly client: DatasetModelClient;
  private readonly orgId: string;
  /** In-flight persist, so concurrent first saves make one create call. */
  private persisting: Promise<Result<void, string>> | null = null;
  /**
   * How a run reaches the grid, registered by the mounted preview.
   *
   * The model owns the query but not the DuckDB session that answers it, so
   * this is the seam: it lets an action run the query without knowing how, and
   * its absence is how the action knows there is no preview to run against.
   *
   * Resolves when that run has settled, with enough of the outcome for a caller
   * that cannot see the grid — an agent, a test — to know whether it worked.
   */
  private previewQueryRunner:
    | ((query: string) => Promise<DatasetPreviewQueryResult>)
    | null = null;

  constructor(props: DatasetModelProps) {
    const { datasetId, client, watchablesFactory } = props;
    super(datasetId);
    this.datasetId = datasetId;
    this.client = client;
    this.orgId = props.orgId;
    const isNew = props.isNew === true;
    this.isPersistedWatchable = Watchable.fromValue(!isNew);
    this.previewQueryWatchable = Watchable.fromValue(DEFAULT_DATASET_QUERY);
    this.savedPreviewQueryWatchable = Watchable.fromValue(
      DEFAULT_DATASET_QUERY
    );
    this.hasUnsavedChangesWatchable = Watchable.from(
      (get) =>
        get(this.previewQueryWatchable).trim() !==
        get(this.savedPreviewQueryWatchable).trim()
    );
    this.datasetDataWatchable = watchablesFactory.makeResourceUpdatedWatchable<
      DatasetResourceState,
      BroadcastEventPayloadMap[typeof BroadcastEventType.ResourceUpdated]
    >({
      runOnMount: true,
      initialValue: isNew
        ? {
            resource: {
              dataset: newDatasetPlaceholder({
                datasetId,
                orgId: props.orgId,
                userId: props.userId,
              }),
              meta: null,
            },
            status: 'ready',
          }
        : { resource: null, status: 'loading' },
      filter: (payload) =>
        payload?.resourceType === 'dataset' &&
        payload?.resourceId === datasetId,
      equalityFn: datasetStateEquality,
      fn: async (_payload, get) => {
        // Fetching an unsaved dataset would only 404 and replace the
        // placeholder with "not found".
        if (!this.isPersistedWatchable.snapshot()) {
          return get(this.datasetDataWatchable);
        }
        const result = await client.resource.get({
          type: ResourceType.Dataset,
          id: datasetId,
        });
        if (result.isErr()) {
          const appErr =
            result.error.t === 'application' ? result.error.err : null;
          if (
            appErr !== null &&
            (appErr.errorType === 'NotFound' ||
              appErr.errorType === 'Forbidden')
          ) {
            return { resource: null, status: 'not-found' };
          }
          return get(this.datasetDataWatchable);
        }
        if (result.value.type !== 'dataset') {
          return get(this.datasetDataWatchable);
        }
        this.syncPreviewQueryFromResource(result.value.dataset);
        return {
          resource: {
            dataset: result.value.dataset,
            meta: result.value.meta ?? null,
          },
          status: 'ready',
        };
      },
    });

    // Declared after `datasetDataWatchable` because it derives from it, and
    // `Watchable.from` evaluates its selector immediately — reading the field
    // before the line that assigns it throws on construction.
    //
    // Two subscriptions rather than one because the phases arrive on two
    // channels — the saga's over the socket, this tab's upload over the local
    // bus — and neither supersedes the other. Whichever spoke last wins.
    const stamp = (payload: unknown): StampedDatasetImportSignal | null => {
      const signal = datasetImportSignalFromPayload(payload, datasetId);
      return signal === null
        ? null
        : { seq: nextDatasetImportSequence(), signal };
    };
    this._remoteImport = EventWatchable.for<
      StampedDatasetImportSignal | null,
      BroadcastEventPayloadMap[typeof BroadcastEventType.DatasetImport]
    >({
      event: DATASET_IMPORT_EVENT,
      initialValue: null,
      runOnMount: false,
      filter: (payload) => isDatasetImportPayloadFor(payload, datasetId),
      // Phases are few and ordered; dropping one would leave the page showing a
      // step the import has already left.
      serializeRefreshes: true,
      // Self-heal. Every phase is a single event with nothing after it to
      // correct the record, so one dropped `ready` — a socket blip, a
      // reconnect, a tab asleep — strands the page on a spinner over a dataset
      // that finished importing minutes ago. The persisted status is the
      // authority; this is how the page gets back to it.
      reconcile: { cadence: DATASET_IMPORT_RECONCILE_MS },
      fn: async (payload, get) =>
        payload === undefined ? this.reconcileImport(get) : stamp(payload),
    });
    const uploadInFlight = inFlightDatasetUpload(datasetId);
    this._localImport = EventWatchable.for<
      StampedDatasetImportSignal | null,
      unknown
    >({
      event: LOCAL_DATASET_IMPORT_EVENT,
      // A page opened on a dataset whose file is still going up has missed
      // every event so far, and its persisted status says the server is reading
      // — which has not started. Seeded so it shows the upload it is waiting on
      // from the first frame, rather than the step after it until the next
      // progress tick.
      initialValue:
        uploadInFlight === null
          ? null
          : { seq: nextDatasetImportSequence(), signal: uploadInFlight },
      runOnMount: false,
      filter: (payload) => isDatasetImportPayloadFor(payload, datasetId),
      serializeRefreshes: true,
      fn: async (payload) => stamp(payload),
    });
    this.importWatchable = Watchable.from<DatasetImportView | null>((get) => {
      const local = get(this._localImport);
      const remote = get(this._remoteImport);
      // Most recent across both channels. A watchable only remembers its own
      // last value, so without the stamp the local one — which always speaks
      // first — would pin the page to `uploading` for the whole import.
      const live = (local?.seq ?? -1) >= (remote?.seq ?? -1) ? local : remote;
      if (live !== null) {
        return live.signal.phase === 'ready' ? null : live.signal;
      }
      const dataset = get(this.datasetDataWatchable).resource?.dataset;
      return datasetImportViewFromStatus(
        dataset?.importStatus,
        dataset?.importError
      );
    });
  }

  get datasetResourceWatchable(): ReadonlyWatchable<DatasetResourceState> {
    return this.datasetDataWatchable;
  }

  /**
   * Re-fetches the dataset resource from the server, out of band from the
   * broadcast that normally drives it.
   *
   * The preview grid calls this when a parquet read fails: the artifact it holds
   * may have been superseded and pruned out from under it, and the cure is to
   * find out what the current one is now rather than to keep retrying the dead
   * one. Resolves once the watchable has the server's answer, so the caller can
   * read {@link datasetResourceWatchable}'s snapshot and see whether the current
   * artifact actually moved — the value dedupes on an unchanged one, so a
   * re-render is not a reliable signal on its own.
   */
  async reloadResource(): Promise<void> {
    await this.datasetDataWatchable.refresh();
  }

  /** What to call this dataset when naming it to someone, loaded or not. */
  get name(): string {
    return (
      this.datasetDataWatchable.snapshot().resource?.dataset.name ??
      'Untitled dataset'
    );
  }

  get isPersisted(): boolean {
    return this.isPersistedWatchable.snapshot();
  }

  /**
   * Creates the dataset if it does not exist yet, under the id this page is
   * already open on. Everything that writes to a dataset goes through here
   * first, so the first real edit is what brings it into being.
   *
   * Safe to call repeatedly: concurrent callers share one request, and the
   * server returns an existing dataset untouched.
   */
  async persist(params?: {
    columns?: ManualDatasetColumnInput[];
    /** Folder to create the dataset in; only used on the create that persists it. */
    folderId?: string;
  }): Promise<Result<void, string>> {
    if (this.isPersisted) {
      return ok(undefined);
    }
    if (this.persisting !== null) {
      return this.persisting;
    }

    const request = this.client.resource.dataset
      .createManual({
        id: this.datasetId,
        ...(params?.columns !== undefined && { columns: params.columns }),
        ...(params?.folderId !== undefined && { folderId: params.folderId }),
      })
      .then((result) => {
        if (result.isErr()) {
          this.persisting = null;
          return err(
            result.error.t === 'application'
              ? result.error.err.message ?? 'Could not create dataset'
              : 'Could not create dataset'
          );
        }
        this.isPersistedWatchable.set(true);
        this.broadcastUpdated();
        return ok(undefined);
      });
    this.persisting = request;
    return request;
  }

  /**
   * Renames the dataset, creating it first when it has not been persisted yet:
   * naming something is as much a save as giving it a column.
   */
  async rename(name: string): Promise<Result<void, string>> {
    const trimmed = name.trim();
    if (trimmed === '') {
      return err('Dataset name cannot be empty');
    }
    const persisted = await this.persist();
    if (persisted.isErr()) {
      return persisted;
    }
    const result = await this.client.resource.dataset.updateSettings({
      id: this.datasetId,
      name: trimmed,
    });
    if (result.isErr()) {
      return err(
        result.error.t === 'application'
          ? result.error.err.message ?? 'Could not rename dataset'
          : 'Could not rename dataset'
      );
    }
    this.broadcastUpdated();
    return ok(undefined);
  }

  /**
   * The per-column display options saved on the dataset, shared across everyone
   * who opens it. The grid seeds itself from these on mount.
   */
  savedColumnDisplayOptions(): DatasetColumnSettingOutput[] {
    return (
      this.datasetDataWatchable.snapshot().resource?.dataset.columnSettings ??
      []
    );
  }

  /**
   * Persists per-column display options. Shared dataset state rather than a
   * browser-local preference, so it goes to the server like a rename does —
   * creating the dataset first when it has not been saved yet.
   */
  async persistColumnDisplayOptions(
    columns: DatasetColumnSettingOutput[]
  ): Promise<Result<void, string>> {
    const persisted = await this.persist();
    if (persisted.isErr()) {
      return persisted;
    }
    const result = await this.client.resource.dataset.updateColumnSettings({
      id: this.datasetId,
      columns,
    });
    if (result.isErr()) {
      return err(
        result.error.t === 'application'
          ? result.error.err.message ?? 'Could not save display options'
          : 'Could not save display options'
      );
    }
    this.broadcastUpdated();
    return ok(undefined);
  }

  /**
   * The workflows the grid currently expands into output columns, as saved on
   * the dataset. Empty when nothing is expanded or the dataset has not loaded.
   */
  savedExpandedAutomationWorkflowIds(): string[] {
    return (
      this.datasetDataWatchable.snapshot().resource?.dataset
        .expandedAutomationWorkflowIds ?? []
    );
  }

  /**
   * Persists which attached workflows the grid expands into output columns.
   * Shared dataset view state, so it goes to the server like a rename does —
   * creating the dataset first when it has not been saved yet.
   */
  async persistExpandedAutomationWorkflowIds(
    workflowIds: string[]
  ): Promise<Result<void, string>> {
    const persisted = await this.persist();
    if (persisted.isErr()) {
      return persisted;
    }
    const result = await this.client.resource.dataset.updateSettings({
      id: this.datasetId,
      expandedAutomationWorkflowIds: workflowIds,
    });
    if (result.isErr()) {
      return err(
        result.error.t === 'application'
          ? result.error.err.message ?? 'Could not save expanded workflows'
          : 'Could not save expanded workflows'
      );
    }
    this.broadcastUpdated();
    return ok(undefined);
  }

  /** Refetches this page and refreshes any dataset list that is mounted. */
  /**
   * Re-reads the dataset to find out whether an import the page still thinks is
   * running has in fact settled.
   *
   * Only fetches when something looks in flight, so an idle dataset page does
   * not poll. `importing` is left alone — the server agrees with what is on
   * screen, and replacing a specific phase with a generic one would lose detail
   * the events had already established.
   */
  private async reconcileImport(
    get: WatchableGetter
  ): Promise<StampedDatasetImportSignal | null> {
    const current = get(this._remoteImport);
    const local = get(this._localImport);
    const inFlight =
      (current !== null && current.signal.phase !== 'ready') ||
      (local !== null && local.signal.phase !== 'ready');
    if (!inFlight) {
      return current;
    }
    const result = await this.client.resource.get({
      type: ResourceType.Dataset,
      id: this.datasetId,
    });
    if (result.isErr() || result.value.type !== 'dataset') {
      return current;
    }
    const { importStatus, importError } = result.value.dataset;
    if (importStatus === 'importing' || importStatus === undefined) {
      return current;
    }
    // A fresh stamp, so this outranks whatever stale phase is on screen — that
    // is the entire point of the reconcile.
    return {
      seq: nextDatasetImportSequence(),
      signal:
        importStatus === 'failed'
          ? { phase: 'failed', error: importError ?? null }
          : { phase: 'ready' },
    };
  }

  private broadcastUpdated(): void {
    emit(BroadcastEventType.ResourceUpdated, {
      orgId: this.orgId,
      resourceType: 'dataset',
      resourceId: this.datasetId,
    });
  }

  /**
   * Adopts the persisted query. A local edit is left alone so a background
   * resource refresh cannot silently discard what the user is typing.
   */
  private syncPreviewQueryFromResource(dataset: DatasetOutput): void {
    const saved = dataset.preview.queryable
      ? dataset.preview.query ?? DEFAULT_DATASET_QUERY
      : DEFAULT_DATASET_QUERY;
    const hadUnsavedEdit = this.hasUnsavedChangesWatchable.snapshot();
    this.savedPreviewQueryWatchable.set(saved);
    if (!hadUnsavedEdit) {
      this.previewQueryWatchable.set(saved);
    }
  }

  setPreviewQuery(query: string): void {
    this.previewQueryWatchable.set(query);
  }

  /**
   * Connects a mounted preview grid, and disconnects it on unmount.
   *
   * Only the current registration clears itself, so a remount that registers
   * before the old grid tears down does not end up unregistered.
   */
  registerPreviewQueryRunner(
    run: (query: string) => Promise<DatasetPreviewQueryResult>
  ): () => void {
    this.previewQueryRunner = run;
    return () => {
      if (this.previewQueryRunner === run) {
        this.previewQueryRunner = null;
      }
    };
  }

  /** False when no preview grid is mounted, so there is nothing to run. */
  get canRunPreviewQuery(): boolean {
    return this.previewQueryRunner !== null;
  }

  /**
   * Runs a query against the preview, adopting it as the page's query first
   * when it differs.
   *
   * The query box keeps a local draft until it is run, so what the user is
   * looking at is not what the model holds. Taking the draft here is what makes
   * "edit then run" and "run this" the same act — unsaved indicator and Ctrl+S
   * included. Omitting it re-runs whatever is already applied.
   *
   * Null means there was no grid to run against; otherwise the result of the
   * run, once it has settled.
   */
  runPreviewQuery(query?: string): Promise<DatasetPreviewQueryResult> | null {
    const runner = this.previewQueryRunner;
    if (runner === null) {
      return null;
    }
    const next = query ?? this.previewQueryWatchable.snapshot();
    if (next !== this.previewQueryWatchable.snapshot()) {
      this.setPreviewQuery(next);
    }
    return runner(next);
  }

  markPreviewQuerySaved(query: string): void {
    this.savedPreviewQueryWatchable.set(query);
    this.previewQueryWatchable.set(query);
  }

  get isTargeted(): boolean {
    return this._isTargeted;
  }

  target(): void {
    this._isTargeted = true;
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  developerDetails(): DeveloperDetails {
    const dataset = this.datasetDataWatchable.snapshot().resource?.dataset;
    const entries = [
      { key: 'Dataset ID', value: this.datasetId },
      { key: 'Org ID', value: this.orgId },
    ];

    if (dataset !== undefined) {
      entries.push({ key: 'Owner ID', value: dataset.ownerId });
      entries.push({ key: 'Type', value: dataset.type });
      entries.push({ key: 'Status', value: dataset.status });
      if (dataset.importStatus !== undefined) {
        entries.push({ key: 'Import status', value: dataset.importStatus });
      }
      const artifacts = dataset.artifacts ?? [];
      artifacts.forEach((artifact, index) => {
        const label =
          artifacts.length > 1 ? `Artifact ${index + 1} ID` : 'Artifact ID';
        entries.push({ key: label, value: artifact.id });
      });
    }

    return {
      title: 'Dataset',
      entries,
    };
  }
}

const datasetModelContext = createProvidedModelContext(
  DatasetModel,
  'dataset-model'
);

export const DatasetModelProvider = datasetModelContext.Provider;
export const useDatasetModel = datasetModelContext.useModel;
export const useOptionalDatasetModel = datasetModelContext.useModelOrNull;
